import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import Button from "./button.core";
import DeleteIcon from "../../../assets/svgs/common/delete.svg";
import LinkIcon from "../../assets/svgs/link.svg";
import EditIcon from "../../assets/svgs/edit.svg";
import ArrowTopRightIcon from "../../../assets/svgs/common/arrow-top-right.svg";

type LinkUploadProps<T extends FieldValues> = {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  placeholder?: string;
  description?: string;
  buttonLabel?: string;
  onUploadClick?: () => void;
  onEdit?: (index: number) => void; // Add edit handler
};

const LinkUpload = <T extends FieldValues>({
  form,
  name,
  label,
  placeholder = "Add links",
  description = "Reference links for creator",
  buttonLabel = "Add more",
  onUploadClick,
  onEdit, // Add edit prop
}: LinkUploadProps<T>) => {
  const fieldValue = form.watch(name) as
    | Array<{ title: string; url: string; notes?: string }>
    | undefined;
  const links = fieldValue || [];

  const handleClearUpload = (index: number) => {
    const currentLinks = form.getValues(name) as Array<{
      title: string;
      url: string;
      notes?: string;
    }>;
    const updatedLinks = currentLinks.filter((_, i) => i !== index);
    form.setValue(name, updatedLinks as T[Path<T>], {
      shouldDirty: true,
    });
  };

  const error = form.formState.errors[name]?.message as string | undefined;
  const hasLinks = links.length > 0;

  return (
    <div className="flex flex-col gap-4 p-6 border-2 border-gray-300 border-dashed rounded-xl w-full min-h-[500px] max-h-[500px] overflow-y-auto">
      <p className="font-medium text-base">{label}</p>

      {/* Links List */}
      {hasLinks && (
        <div className="flex flex-col gap-4">
          {links.map((item, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            <div key={index} className="flex flex-col gap-3">
              <div className="flex justify-between items-start gap-4 px-4 py-3">
                <div className="flex flex-1 items-start gap-3 min-w-0">
                  <ArrowTopRightIcon className="w-10 h-10" />

                  <div className="flex flex-col flex-1 gap-1 min-w-0">
                    <p className="font-medium text-sm">{item.title}</p>
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-wrap hover:underline break-all"
                    >
                      {item.url}
                    </a>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onEdit?.(index)} // Call edit handler
                    className="rounded-lg transition-colors"
                  >
                    <EditIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleClearUpload(index)}
                    className="rounded-lg text-red-500 transition-colors"
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>

              {/* Notes Display */}
              {item.notes && (
                <p className="bg-gray-50 p-5 rounded-xl text-wrap">
                  {item.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!hasLinks && (
        <div className="flex flex-col flex-1 justify-center items-center gap-2">
          <LinkIcon />
          <p className="font-medium text-base">{placeholder}</p>
          <p className="text-gray-400 text-sm">{description}</p>
        </div>
      )}

      {/* Upload Button */}
      <div className={hasLinks ? "" : "mt-auto"}>
        <Button
          type="button"
          onClick={onUploadClick}
          variant="secondary"
          className="px-6 py-3 border-2 border-black rounded-xl w-full font-semibold text-base"
        >
          {hasLinks ? buttonLabel : placeholder}
        </Button>
      </div>

      {error && <p className="text-red-500 text-sm">{error}</p>}
    </div>
  );
};

export default LinkUpload;
